import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { Marked } from 'marked';

import {
    AGENT_MARKDOWN_RENDERER_OPTIONS,
    INCOMPLETE_MARKDOWN_LINK_REGEX,
} from 'AgentApp/ts/constants/agent-markdown.constants';
import { AgentStepDescription } from 'AgentApp/ts/interfaces/agent-step-description.interface';

import { IconModule } from 'Angular/features/directives/icon/icon.module';

const marked = new Marked({ renderer: { ...AGENT_MARKDOWN_RENDERER_OPTIONS } });

const MAX_VISIBLE_STEP_ITEMS = 5;

@Component({
    selector: 'ycn-agent-steps',
    imports: [IconModule],
    templateUrl: './agent-steps.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentStepsComponent {
    readonly steps = input.required<AgentStepDescription[]>();

    readonly streaming = input(false);

    /**
     * Whether the agent turn these steps belong to has finished. Steps flushed onto a message while the
     * response is still streaming are complete themselves, but the run isn't, so no "Done" row yet
     */
    readonly turnComplete = input(true);

    readonly expanded = input(true);

    readonly toggled = output<boolean>();

    protected readonly showSteps = linkedSignal(() => this.expanded());

    // Thinking blocks can contain markdown, so render their description as HTML the same way
    // assistant text blocks do (see AgentResponseMessageComponent). tool_call steps carry no
    // description, so this only affects "Thought" steps.
    protected readonly renderedSteps = computed(() =>
        this.steps().map((step) => {
            const items = step.items ?? [];
            return {
                ...step,
                visibleItems: items.slice(0, MAX_VISIBLE_STEP_ITEMS),
                hiddenItemCount: Math.max(0, items.length - MAX_VISIBLE_STEP_ITEMS),
                descriptionHtml: step.description
                    ? (marked.parse(
                          step.description.replace(INCOMPLETE_MARKDOWN_LINK_REGEX, '*Creating Link...*')
                      ) as string)
                    : '',
            };
        })
    );

    toggleSteps(): void {
        const next = !this.showSteps();
        this.showSteps.set(next);
        this.toggled.emit(next);
    }
}
